<template>
  <div
    v-show="localShowNotesPanel"
    class="notes-panel"
    draggable="true"
    @dragstart="dragStart"
    @dragend="dragEnd"
    @dragover.prevent
    ref="notesPanel"
  >
    <div class="notes-header">
      <span class="header-title"> Notes for selected {{ noteType }} </span>
      <button class="notes-close-button" @click="closeNotesPanel">X</button>
    </div>
    <div class="search-container">
      <input
        ref="searchInputN"
        v-model="noteSearchQuery"
        placeholder="Search notes"
        class="search-input"
      />
      <button @click="clearNoteSearch()" class="notes-search-button">
        Clear
      </button>
      <button @click="openNoteDialog()" class="notes-search-button">New</button>
    </div>
    <div class="notes-container">
      <div v-if="filteredNotes.length === 0" class="no-notes-message">
        Click plus to add a new note
      </div>
      <div
        v-else
        v-for="note in filteredNotes"
        :key="note.Id"
        class="note-card"
      >
        <div class="note-title">{{ note.noteTitle }}</div>
        <div
          class="note-body"
          v-html="
            highlightMatch(renderMarkDown(note.noteBody, noteSearchQuery))
          "
          @dblclick="openNoteDialog(note)"
        ></div>
        <div class="note-footer">
          <button @click="openNoteDialog(note)" class="note-button">
            <i class="fas fa-ellipsis-h"></i>
          </button>
          <span class="note-details"
            >{{ note.ModifiedBy }} {{ formatDateTime(note.ModifiedOn) }}</span
          >
        </div>
      </div>
    </div>

    <!-- Note Dialog -->
    <div v-if="showNoteDialog" class="dialog-overlay">
      <div class="notes-dialog">
        <div class="notes-editor-header">
          <div>{{ editingNote ? 'Edit Note' : 'Add Note' }}</div>
        </div>
        <input
          ref="newInputN"
          type="text"
          v-model="newNoteTitle"
          placeholder="Note Title"
          class="note-title-input"
        />
        <textarea
          v-model="newNoteBody"
          placeholder="Note Body"
          class="note-textarea"
        ></textarea>
        <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank"
          >markdown help</a
        >
        <div class="dialog-buttons">
          <button @click="saveNote">Save</button>
          <button @click="closeNoteDialog">Close</button>
          <button v-if="editingNote" @click="deleteNote" class="delete-button">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import axios from '@/axiosConfig'; // Adjust based on your axios configuration

export default {
  props: ['showNotesPanel', 'noteType', 'noteId'],
  data() {
    return {
      localShowNotesPanel: this.showNotesPanel,
      editingNote: null,
      showNoteDialog: false,
      newNoteTitle: '',
      newNoteBody: '',
      noteSearchQuery: '',
      dragOffset: { x: 0, y: 0 },
      notes: [],
    };
  },
  computed: {
    filteredNotes() {
      return this.filterNotes(this.noteSearchQuery);
    },
  },
  methods: {
    onShowNotesPanel() {
      this.$nextTick(() => {
        this.centerNotesPanel();
        this.fetchNotes(this.noteId, this.noteType);
        this.$refs.searchInputN.focus();
      });
    },

    dragStart(event) {
      const element = event.target;
      this.dragOffset.x = event.clientX - element.getBoundingClientRect().left;
      this.dragOffset.y = event.clientY - element.getBoundingClientRect().top;
    },

    dragEnd(event) {
      const element = event.target;

      const left = event.clientX - this.dragOffset.x;
      const top = event.clientY - this.dragOffset.y;

      const parent = element.offsetParent || document.body;
      const parentWidth = parent.clientWidth;
      const parentHeight = parent.clientHeight;

      const newLeft = Math.min(
        Math.max(left, 0),
        parentWidth - element.clientWidth,
      );
      const newTop = Math.min(
        Math.max(top, 0),
        parentHeight - element.clientHeight,
      );

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;
    },

    openNoteDialog(note = null) {
      this.editingNote = note;
      if (note) {
        this.newNoteTitle = note.noteTitle;
        this.newNoteBody = note.noteBody;
      } else {
        this.newNoteTitle = '';
        this.newNoteBody = '';
      }
      this.showNoteDialog = true;
      this.$nextTick(() => {
        this.$refs.newInputN.focus();
      });
    },

    closeNotesPanel() {
      this.localShowNotesPanel = false;
      this.$emit('update-show-notes-panel', false); // Emit event to parent
    },

    closeNoteDialog() {
      this.showNoteDialog = false;
      this.$nextTick(() => {
        this.$refs.searchInputN.focus();
      });
    },

    async fetchNotes(id, noteType) {
      if (!id) {
        console.error('ID is required to fetch notes.');
        return;
      }

      axios
        .get('/api/notes', {
          params: {
            Id: id,
            noteType: noteType,
          },
        })
        .then((response) => {
          this.notes = response.data;
        })
        .catch((error) => {
          console.error('Error fetching notes:', error);
        });
    },

    async saveNote() {
      const noteType = this.noteType; // Use the noteType stored in the component
      const url = this.editingNote
        ? `/api/note/${this.editingNote.Id}`
        : '/api/note';
      const method = this.editingNote ? 'PUT' : 'POST';

      const noteData = {
        noteType: noteType, // Include noteType in the data
        Id: this.noteId,
        noteTitle: this.newNoteTitle,
        noteBody: this.newNoteBody,
      };

      axios({
        method: method,
        url: url,
        data: noteData,
      })
        .then(() => {
          this.closeNoteDialog();
          this.fetchNotes(this.noteId, this.noteType);
        })
        .catch((error) => {
          console.error('Error saving note:', error);
        });
    },

    async deleteNote() {
      if (this.editingNote) {
        const noteType = this.noteType; // Use the noteType stored in the component

        axios
          .delete(`/api/note/${this.editingNote.Id}`, {
            params: { noteType: noteType }, // Include noteType as a query parameter
          })
          .then(() => {
            this.closeNoteDialog();
            this.fetchNotes(this.noteId, this.noteType);
          })
          .catch((error) => {
            console.error('Error deleting note:', error);
          });
      }
    },

    centerNotesPanel() {
      this.$nextTick(() => {
        const element = this.$refs.notesPanel;

        if (!element) {
          console.error('notesPanel ref is undefined');
          return;
        }

        const parent = element.offsetParent;
        if (parent) {
          const parentWidth = parent.clientWidth;
          const parentHeight = parent.clientHeight;

          // Calculate centered position
          const left = (parentWidth - element.clientWidth) / 2;
          const top = (parentHeight - element.clientHeight) / 2;

          // Apply the calculated position
          element.style.left = `${left}px`;
          element.style.top = `${top}px`;
        }
      });
    },

    filterNotes(query) {
      if (!query) return this.notes; // Return all notes if the query is empty

      const lowerQuery = query.toLowerCase();
      return this.notes.filter(
        (note) =>
          note.noteBody.toLowerCase().includes(lowerQuery) ||
          note.noteTitle.toLowerCase().includes(lowerQuery),
      );
    },

    clearNoteSearch() {
      this.noteSearchQuery = '';
    },

    renderMarkDown(noteBody) {
      if (!noteBody) {
        return ''; // Return an empty string if noteBody is undefined or null
      }

      try {
        const markdown = marked.parse(noteBody); // Ensure this exists and is correctly set up
        return markdown;
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return noteBody; // Fallback to displaying the raw note body if parsing fails
      }
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    highlightMatch(text, query) {
      if (!text || !query) {
        return text;
      }

      const escapedQuery = this.escapeRegExp(this.searchQuery);
      const re = new RegExp(`(${escapedQuery})`, 'gi');
      if (text)
        return text.replace(
          re,
          (match) => `<b class="highlight-match">${match}</b>`,
        );
    },

    formatDateTime(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
  },
  watch: {
    showNotesPanel(newVal) {
      this.localShowNotesPanel = newVal; // Sync local data with prop
      if (newVal) {
        this.onShowNotesPanel();
      }
    },
  },
};
</script>

<style scoped>
.search-input {
  width: 457px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.edit-dialog-body .search-input {
  width: 170px !important;
  margin-left: 12px;
}
.note-title {
  font-weight: bold;
  margin-bottom: 5px;
}
.note-title-input {
  width: calc(100% - 20px);
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}
.notes-panel {
  position: absolute;
  top: 50%;
  left: 50%;
  /* transform: translate(-50%, -50%); */
  background-color: #e5e5e5;
  border: 1pt solid #888;
  border-radius: 8px;
  box-shadow: 5px 10px 9px 1px rgb(0 0 0 / 22%);
  z-index: 10001;
  width: 400px;
  padding: 20px;
  overflow: hidden;
  height: 60%;
}
.search-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  width: 95%;
  margin-top: 20px;
}
.notes-container {
  max-height: 98%;
  overflow-y: auto;
}
.note-card {
  padding: 10px;
  background-color: #f0f3d8;
  border: 1pt solid #ccc;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  width: 90%;
  font-size: 0.8em;
}
.note-title-bar {
  display: flex;
  justify-content: flex-end;
}
.note-body {
  margin: 10px 0;
}
.note-footer {
  color: #888;
  text-align: right;
  font-size: 0.8em;
  font-style: italic;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #d5cfcf;
  padding-top: 6px;
}
.notes-header {
  color: white;
  margin-bottom: 3px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 24px;
  background: #575757;
  padding-left: 23px;
  padding-top: 5px;
}
.note-button {
  margin-right: auto;
  width: 23px;
  height: 15px;
  font-size: 0.7em;
  background-color: #d5d5d5;
  border: 1pt solid #888;
  border-radius: 4px;
}
.note-details {
  text-align: right;
  font-size: 0.85em;
}
.notes-search-button {
  font-size: 0.7em;
  margin-left: 3px;
}

/* for file upload dialog */
.notes-dialog {
  position: absolute;
  top: 15%;
  left: 15%;
  background: #c7c3c3;
  padding: 20px;
  box-shadow: 14px 7px 10px rgba(0, 0, 0, 0.1);
  z-index: 1002;
  width: 400px;
  border-radius: 4px;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.notes-editor-header {
  background: #333;
  color: #eee;
  margin-top: -20px;
  margin-bottom: 20px;
  padding: 10px;
  padding-left: 20px;
  margin-left: -20px;
  margin-right: -20px;
  border-radius: 4px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.notes-dialog input textarea {
  background: lightyellow !important;
}

.note-title-input {
  width: calc(100% - 20px);
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.note-textarea {
  width: calc(100% - 20px);
  height: 200px;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.dialog-buttons {
  margin-top: 20px;
  text-align: right;
  border-radius: 4px;
  border-color: #ddd;
}

.dialog-buttons .delete-button {
  background-color: #e99ea6;
}

.notes-close-button {
  text-align: center;
  position: absolute;
  right: 36px;
  top: 5px;
  font-size: 0.8em;
  width: 25px;
}
</style>
